<template>
    <div class="lms__superadmin_org_units_edit">
        <div class="lms__superadmin_org_units_edit_header">
            <view-actions
                :title="'Organisation units'"
                :notify-title="'organisation units'"
                :notify-process="'cancel editing'"
                :cancel-button-text="'Cancel'"
                :submit-button-active="false"
                @emit-notify-cancel-submit="orgUnitStopEdit"
            />
        </div>
        <div class="lms__superadmin_org_units_edit_main">
            <div class="container">
                <div class="columns is-variable">
                    <div class="column">
                        <div class="lms__superadmin_org_units_edit_main_filters">
                            <template v-if="user.isSuperadmin() && !loggedInUserWorkspaceId">
                                <form-select
                                    :disabled="requestPending"
                                    :name="'org-unit-customer'"
                                    :label="'Customer'"
                                    :is-type="'select'"
                                    :required="true"
                                    :value="orgUnitCustomerId"
                                    :v-validate="'required'"
                                    :data-vv-as="'customer'"
                                    :get-pagination-getter="user.isSuperadmin() ? 'getLmsSuperadminCustomersPagination' : ''"
                                    :get-items-getter="user.isSuperadmin() ? 'getLmsSuperadminCustomersMappedIdNameStatus' : 'getLmsSuperadminUserProfilesCustomersMappedIdName'"
                                    :get-items-getter-mapped="user.isSuperadmin() ? 'getLmsSuperadminCustomersMappedLabelValue' : 'getLmsSuperadminUserProfilesCustomersMappedLabelValue'"
                                    :get-items-dispatch="user.isSuperadmin() ? 'lmsSuperadminCustomersGet' : ''"
                                    :items-extra="loggedInUserCustomerName && loggedInUserCustomerId ? [{
                                        label: loggedInUserCustomerName + loggedInUserCustomerId,
                                        customLabel: loggedInUserCustomerName,
                                        value: loggedInUserCustomerId
                                    }] : []"
                                    :item-name="['customer', 'customers']"
                                    :search-queryable="user.isSuperadmin()"
                                    :table-labels="customerTableLabels"
                                    :prevent-clear="true"
                                    @input="setCustomerId"
                                    @input-table="setCustomerId"
                                    @get-items-current="handleGetCustomerCurrent"
                                    @request-selected="setWorkspaces"
                                />
                            </template>
                            <template v-else>
                                <form-text
                                    :label="'Customer'"
                                    :name="'org-unit-customer-readonly'"
                                    :read-only="true"
                                    :value="loggedInUserCustomerName"
                                ></form-text>
                            </template>
                            <template v-if="!loggedInUserWorkspaceId">
                                <form-select
                                    :key="orgUnitCustomerId"
                                    :disabled="!orgUnitCustomerId || requestPending"
                                    :name="'org-unit-workspace'"
                                    :label="'Workspace'"
                                    :is-type="'select'"
                                    :required="true"
                                    :value="orgUnitWorkspaceId"
                                    :data-vv-as="'workspace'"
                                    :get-items-dispatch="!user.isSuperadmin() && user.isMasterAdministrator() && orgUnitCustomerId && profileCustomerIdFromCustomerWithMARole ? 'lmsSuperadminCustomerGet' : ''"
                                    :get-items-dispatch-data="orgUnitCustomerId"
                                    :get-items-getter="user.isSuperadmin() ? 'getLmsSuperadminWorkspacesMappedIdNameStatus' : (user.isMasterAdministrator() && profileCustomerIdFromCustomerWithMARole ? 'getLmsSuperadminCustomerWorkspacesMappedIdNameStatus' : (orgUnitCustomerId ? 'getLmsSuperadminUserProfilesByCustomerMappedIdName' : ''))"
                                    :get-items-getter-params="(!user.isSuperadmin() && !user.isMasterAdministrator()) || (!user.isSuperadmin() && user.isMasterAdministrator() && !profileCustomerIdFromCustomerWithMARole) ? orgUnitCustomerId : ''"
                                    :get-items-getter-mapped="user.isSuperadmin() ? 'getLmsSuperadminWorkspacesMappedLabelValue' : (user.isMasterAdministrator() && profileCustomerIdFromCustomerWithMARole ? 'getLmsSuperadminCustomerWorkspacesMappedLabelValue' : (orgUnitCustomerId ? 'getLmsSuperadminUserProfilesByCustomerMappedLabelValue' : ''))"
                                    :get-items-getter-mapped-params="(!user.isSuperadmin() && !user.isMasterAdministrator()) || (!user.isSuperadmin() && user.isMasterAdministrator() && !profileCustomerIdFromCustomerWithMARole) ? orgUnitCustomerId : ''"
                                    :item-name="['workspace', 'workspaces']"
                                    :prevent-clear="true"
                                    :table-labels="[
                                    {
                                        key: 'name',
                                        title: 'Name',
                                        type: 'string',
                                        sortable: true,
                                        queryable: true
                                    },
                                    {
                                        key: 'customer',
                                        title: 'Customer',
                                        type: 'string'
                                    }
                                ]"
                                    @input="setWorkspaceId"
                                    @input-table="setWorkspaceId"
                                />
                            </template>
                            <template v-else>
                                <form-text
                                    :label="'Workspace'"
                                    :name="'org-unit-workspace-readonly'"
                                    :read-only="true"
                                    :value="loggedInUserWorkspaceName"
                                ></form-text>
                            </template>
                        </div>
                        <div class="lms__superadmin_org_units_edit_main_fields">
                            <template v-if="requestPending">
                                <component-wrapper>
                                    <component-loading :size="'small'"></component-loading>
                                </component-wrapper>
                            </template>
                            <template v-else>
                                <template v-if="orgUnitWorkspaceId">
                                    <component-note
                                        v-if="isAPIMode"
                                    >Your API is currently in use. Refer to your API to manage organisation units.
                                    </component-note>
                                    <component-note
                                        v-show="!workspaceApiStateRequestPending && !isAPIMode"
                                        v-if="(!orgUnitId && orgUnits.length) || (orgUnitId && !orgUnits.length) || (orgUnitId && !orgUnits[0].id)"
                                    >There are no organisation units in this workspace. Create an organisation unit by typing the name in the field below and click save unit.
                                    </component-note>
                                    <form-orgunits
                                        v-show="!workspaceApiStateRequestPending"
                                        :key="rerenderIndex"
                                        :org-units="orgUnits"
                                        :disabled="requestPending || (!user.isSuperadmin() && !user.isMasterAdministrator() || isAPIMode)"
                                        @emit-add="performAddOrgUnit"
                                        @emit-remove="performRemoveOrgUnit"
                                        @emit-move="performMoveOrgUnit"
                                    ></form-orgunits>
                                </template>
                                <template v-else>
                                    <component-note v-if="!requestPending">To manage organisation unit select the customer and the workspace first.</component-note>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import ViewActions from '@/components/default/shared/ViewActions'
import FormOrgunits from '@/components/default/forms/FormOrgUnits'
import FormSelect from '@/components/default/forms/FormSelect'
import { User } from '@/User'
import ComponentNote from '@/components/default/shared/ComponentNote'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'
import FormText from '@/components/default/forms/FormText'

export default {
  name: 'lms-superadmin-org-units-edit',
  components: { FormText, ComponentWrapper, ComponentLoading, ComponentNote, FormSelect, FormOrgunits, ViewActions },
  data () {
    return {
      user: new User(),
      rerenderIndex: 0,
      requestPending: false,
      workspaceApiState: '',
      workspaceApiStateRequestPending: false
    }
  },
  computed: {
    loggedInUserCustomerId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    loggedInUserCustomerName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerName
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    loggedInUserWorkspaceName () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceName
    },
    profileCustomerIdFromCustomerWithMARole () {
      const customers = this.$store.getters.getLmsSuperadminUserProfilesCustomersWithMARole
      return customers.findIndex(obj => obj.customerId === this.filterCustomerId) > -1
    },
    orgUnits () {
      return this.$store.getters.getLmsSuperadminOrgUnitsChildren
    },
    orgUnitId () {
      return this.$store.getters.getLmsSuperadminOrgUnitsId
    },
    orgUnitCustomerId () {
      return this.$store.getters.getLmsSuperadminOrgUnitsCustomCustomerId
    },
    orgUnitWorkspaceId () {
      return this.$store.getters.getLmsSuperadminOrgUnitsCustomWorkspaceId
    },
    customerTableLabels () {
      let labelsArr = []

      labelsArr.push({
        key: 'name',
        title: 'Name',
        type: 'string',
        sortable: true,
        queryable: true
      })

      return labelsArr
    },
    isAPIMode () {
      return Boolean(this.workspaceApiState && this.workspaceApiState.service && this.workspaceApiState.api)
    }
  },
  methods: {
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('org-unit-submit')
    },
    //
    orgUnitStopEdit: function () {
      this.allowRedirectBeforeRouteLeave()
      this.$router.push({ name: 'lms-superadmin-user-list' })
    },
    performAddOrgUnit (data) {
      this.$store.dispatch('lmsSuperadminOrgUnitsAdd', data)
    },
    performRemoveOrgUnit (data) {
      this.rerenderIndex++
      this.requestPending = true
      this.$store.dispatch('lmsSuperadminOrgUnitRemove', data).then(() => {
        if (data && data.id) {
          this.getOrgUnits()
        }
      }).finally(() => {
        this.requestPending = false
      })
    },
    performMoveOrgUnit (data) {
      this.rerenderIndex++
      this.requestPending = true
      this.$store.dispatch('lmsSuperadminOrgUnitMove', data).finally(() => {
        this.requestPending = false
      }).then(() => {
        this.rerenderIndex++
      })
    },
    handleGetCustomerCurrent (data) {
      const selectedCustomer = data.data ? data.data[0] : null

      if (selectedCustomer) {
        this.setWorkspaces({ extraData: selectedCustomer.workspaces })
      }
    },
    setCustomerId (data) {
      this.$store.dispatch('lmsSuperadminOrgUnitsSetCustomCustomerId', data.value)
      this.setWorkspaceId({ value: '' })
      this.setWorkspaces(data)
    },
    setWorkspaces (data) {
      if (data && data.extraData) {
        this.$store.dispatch('lmsSuperadminWorkspacesSet', { data: data.extraData })
      }
    },
    setWorkspaceId (data) {
      this.$store.dispatch('lmsSuperadminOrgUnitsSetCustomWorkspaceId', data.value)
      if (data.value) {
        this.switchWorkspace(data.value)
      }
      this.checkWorkspaceApiState(data.value)
    },
    checkWorkspaceApiState (data) {
      if (!data) return

      this.workspaceApiState = ''
      this.workspaceApiStateRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceGetApiIntegrationState', data).then(response => {
        this.workspaceApiState = response
      }).finally(() => {
        this.workspaceApiStateRequestPending = false
      })
    },
    switchWorkspace (data) {
      this.rerenderIndex++
      this.requestPending = true
      this.$store.dispatch('lmsSuperadminOrgUnitsSwitchWorkspace', data).finally(() => {
        this.requestPending = false
      }).then(() => {
        this.rerenderIndex++
      })
    },
    initializeFilterValues () {
      const routeQuery = this.$route.query
      const routeQueryCustomerId = routeQuery ? routeQuery.customerId : ''
      const routeQueryWorkspaceId = routeQuery ? routeQuery.workspaceId : ''

      if (routeQueryCustomerId && routeQueryWorkspaceId) {
        this.setCustomerId({ value: routeQueryCustomerId })
        this.setWorkspaceId({ value: routeQueryWorkspaceId })
      } else if (this.loggedInUserCustomerId) {
        this.setCustomerId({ value: this.loggedInUserCustomerId })
        this.setWorkspaceId({ value: this.loggedInUserWorkspaceId })
      }
    },
    getOrgUnits () {
      this.requestPending = true
      this.$store.dispatch('lmsSuperadminOrgUnitsGet', this.orgUnitWorkspaceId).finally(() => {
        this.requestPending = false
      })
    }
  },
  created () {
    this.initializeFilterValues()
  },
  mounted () {
    this.$eventBus.$on('form-org-units-rerender', () => {
      this.rerenderIndex++
    })
  },
  beforeDestroy () {
    this.$eventBus.$off('form-org-units-rerender')
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/org-units/_lmssuperadminorgunitsaddedit.scss";
</style>
